import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown, X } from 'lucide-react';

export default function TypeaheadSelect({ id, label, required, placeholder, items, value, displayValue, onChange, onAddNew, addNewLabel, showAddNewOption = true, disabled, prefixMatch = false, onRemoveItem, removeItemTitle = 'Remove from list' }) {
  const [inputValue, setInputValue] = useState(displayValue || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Whether to render the explicit "+ Add new" row. Even when hidden, typing
  // a name and pressing Enter still commits it via onAddNew — callers can
  // drop the visual row when "just type the name" is affordance enough.
  const showAddNew = !!onAddNew && showAddNewOption;

  useEffect(() => {
    setInputValue(displayValue || '');
  }, [displayValue]);

  // Default: substring match. prefixMatch narrows to names that *begin with*
  // what's typed, progressively (first letter, then next, …) until no matches.
  const filtered = items.filter(item => {
    const q = inputValue.toLowerCase();
    const n = item.name.toLowerCase();
    return prefixMatch ? n.startsWith(q) : n.includes(q);
  });

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setHighlightIndex(-1);
    if (!e.target.value) {
      onChange('', '');
    }
  };

  const handleSelect = (item) => {
    onChange(item.id, item.name);
    setInputValue(item.name);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }
    const totalItems = filtered.length + (showAddNew ? 1 : 0);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filtered.length) {
        handleSelect(filtered[highlightIndex]);
      } else if (onAddNew && (highlightIndex === filtered.length || (highlightIndex === -1 && inputValue.trim()))) {
        // Add-new fires when its row is highlighted, or — for convenience —
        // when the user has typed text and hits Enter without arrowing down.
        // The latter works even when the visible add-new row is hidden.
        onAddNew(inputValue.trim());
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        if (!value && inputValue) {
          setInputValue('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, inputValue]);

  return (
    <div className="typeahead-wrapper" ref={wrapperRef}>
      <div className="typeahead-input-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          required={required && !value}
        />
        <button
          type="button"
          className="typeahead-toggle"
          onClick={() => { setIsOpen(!isOpen); inputRef.current?.focus(); }}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Toggle dropdown"
        >
          <ChevronDown size={16} />
        </button>
      </div>
      {isOpen && (
        <div className="typeahead-dropdown">
          {filtered.length === 0 && !showAddNew && (
            <div className="typeahead-empty">No matches found</div>
          )}
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              className={`typeahead-option${item.id === value ? ' selected' : ''}${idx === highlightIndex ? ' highlighted' : ''}${onRemoveItem ? ' typeahead-option-removable' : ''}`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIndex(idx)}
            >
              <span className="typeahead-option-label">{item.name}</span>
              {onRemoveItem && (
                <button
                  type="button"
                  className="typeahead-option-remove"
                  title={removeItemTitle}
                  aria-label={`${removeItemTitle}: ${item.name}`}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); onRemoveItem(item); }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
          {showAddNew && (
            <div
              className={`typeahead-option typeahead-add-new${highlightIndex === filtered.length ? ' highlighted' : ''}`}
              onClick={() => { onAddNew(inputValue.trim()); setIsOpen(false); }}
              onMouseEnter={() => setHighlightIndex(filtered.length)}
            >
              <Plus size={14} /> {typeof addNewLabel === 'function' ? addNewLabel(inputValue.trim()) : (addNewLabel || 'Add New')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
